// src/views/admin/HumanVerificationWidget.js
import React from 'react';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Heading,
  Button,
  Badge,
  Icon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  useColorModeValue,
  Tooltip
} from '@chakra-ui/react';
import { 
  MdGrading, 
  MdPendingActions, 
  MdCheckCircle,
  MdInfoOutline
} from 'react-icons/md';

const HumanVerificationWidget = ({ 
  reviewQueue = [], 
  verifiedCount = 0, 
  onReviewClick,
  isLoading = false 
}) => {
  // 1. Calculate the condition unconditionally
  const pendingCount = reviewQueue.length;
  const hasPending = pendingCount > 0;

  // 2. Call all Hooks unconditionally at the top level
  const cardBg = useColorModeValue('white', 'gray.800');
  const headingColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  
  // ✅ FIXED: Pass the condition inside the hook parameters instead of the other way around
  const borderColor = useColorModeValue(
    hasPending ? 'orange.200' : 'gray.200',
    hasPending ? 'orange.700' : 'gray.600'
  );

  const statPendingBg = useColorModeValue('orange.50', 'orange.900');
  const statPendingBorder = useColorModeValue('orange.100', 'orange.800');
  const statVerifiedBg = useColorModeValue('green.50', 'green.900');
  const statVerifiedBorder = useColorModeValue('green.100', 'green.800');

  return (
    <Box
      bg={cardBg}
      p={6}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="md"
      transition="all 0.3s ease"
    >
      <Flex justify="space-between" align="center" mb={4}>
        <HStack>
          <Icon 
            as={MdGrading} 
            boxSize={6} 
            color={hasPending ? "orange.500" : "green.500"} 
          />
          <Heading size="md" color={headingColor}>
            Human Verification Loop
          </Heading>
          <Tooltip label="Reinforcement Learning from Human Feedback (RLHF)" hasArrow>
            <Box as="span" color={mutedColor} cursor="help" mt={1}>
              <MdInfoOutline size={14} />
            </Box>
          </Tooltip>
        </HStack>
        {hasPending ? (
          <Badge colorScheme="orange">{pendingCount} Action Required</Badge>
        ) : (
          <Badge colorScheme="green">Caught Up</Badge>
        )}
      </Flex>

      <Text fontSize="sm" color={mutedColor} mb={6}>
        The AI flags borderline predictions for human review. Your feedback is added to the Golden Dataset to improve future model accuracy.
      </Text>

      {/* Stats Grid */}
      <SimpleGrid columns={2} spacing={4} mb={6}>
        <Stat 
          bg={statPendingBg} 
          p={3} 
          borderRadius="md" 
          borderWidth="1px" 
          borderColor={statPendingBorder}
        >
          <Flex align="center" mb={1}>
            <Icon as={MdPendingActions} color="orange.500" mr={2} />
            <StatLabel fontSize="xs" fontWeight="bold" color="orange.600" _dark={{ color: 'orange.300' }}>
              Pending Review
            </StatLabel>
          </Flex>
          <StatNumber fontSize="2xl" color="orange.600" _dark={{ color: 'orange.300' }}>
            {pendingCount}
          </StatNumber>
        </Stat>

        <Stat 
          bg={statVerifiedBg} 
          p={3} 
          borderRadius="md" 
          borderWidth="1px" 
          borderColor={statVerifiedBorder}
        >
          <Flex align="center" mb={1}>
            <Icon as={MdCheckCircle} color="green.500" mr={2} />
            <StatLabel fontSize="xs" fontWeight="bold" color="green.600" _dark={{ color: 'green.300' }}>
              Human Verified
            </StatLabel>
          </Flex>
          <StatNumber fontSize="2xl" color="green.600" _dark={{ color: 'green.300' }}>
            {verifiedCount}
          </StatNumber>
        </Stat>
      </SimpleGrid>

      <Button 
        colorScheme={hasPending ? "orange" : "gray"} 
        variant={hasPending ? "solid" : "outline"} 
        w="full" 
        size="sm"
        onClick={onReviewClick}
        isDisabled={!hasPending}
        isLoading={isLoading}
      >
        {hasPending ? 'Review AI Predictions' : 'No Reviews Pending'}
      </Button>
    </Box>
  );
};

export default HumanVerificationWidget;